/**
 * How many changes are waiting, on the rail.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE RAIL AND NOT THE PAGE
 *
 * The whole defect the Drafts page answers is that a staged change was visible
 * only to somebody already looking at the screen it was staged on. A page fixes
 * that for anybody who thinks to open it. The number is what makes somebody
 * think to open it, and the rail is the only surface on screen at the time.
 *
 * IT COUNTS LIVE ROWS ONLY. `/drafts/count` asks for PENDING with an unexpired
 * window, which is the same pair of conditions `executeApproval` claims on, so
 * the badge can never say 4 about four things that cannot be applied. A number
 * that is not actionable is worse than no number: it sends a person to a page
 * to do nothing and teaches them to stop looking.
 *
 * NOTHING WHEN THERE IS NOTHING. No grey zero, which is a permanent small alarm
 * that means "all clear" and reads as "something".
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useCan } from '@/lib/permissions';
import { cn } from '@/lib/utils';

/**
 * Half a minute.
 *
 * A draft arrives from a conversation somebody else is having, so nothing on
 * this client can know when to refetch. Thirty seconds is well inside the
 * two-hour window a draft lives for, and it is one small count query.
 */
const REFETCH_MS = 30_000;

export function DraftsBadge({ className }: { className?: string }) {
  const can = useCan();
  const allowed = can('drafts.read');

  const { data } = useQuery({
    queryKey: ['drafts', 'count'],
    queryFn: () => api<{ pending: number }>('/drafts/count'),
    enabled: allowed,
    refetchInterval: REFETCH_MS,
    /* A rail is not a page. A failed count must be silent, never a retry storm
       behind a number nobody asked for. */
    retry: false,
  });

  const pending = data?.pending ?? 0;
  if (!allowed || pending === 0) return null;

  return (
    <span
      /* `num` is the tabular-figures class the rest of the app uses for
         quantities, so 9 and 11 do not shuffle the row width as the count
         changes under a reader's eyes. */
      className={cn(
        'num ms-auto shrink-0 min-w-5 rounded-full px-1.5 py-0.5 text-center text-[10px] leading-none font-medium',
        'bg-sidebar-primary text-sidebar-primary-foreground',
        className,
      )}
    >
      {pending > 99 ? '99+' : pending}
    </span>
  );
}
